import { useEffect, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { Button } from "@/components/design/Button";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { logApplicationFailure } from "@/lib/user-error";
import {
  estimateValuation,
  type TobisValuationComparable,
  type TobisValuationEstimate,
  type TobisValuationManualInput,
} from "@/lib/tobis";
import { TobisValuationDistribution } from "./TobisValuationDistribution";

// Bewertungsschritt im Labor-Result (siehe apps/bff/src/tobis/valuation.ts fuer
// die Formel selbst - hier nur Anzeige plus die Eingaben, die die Rechnung
// braucht). `comparables` kommt jetzt von TobisPitchController.findComparables
// (agent-service): EIN KI-Call waehlt die wirklich vergleichbaren Titel schon
// aus echtem Kontext (Filmografien + Themen-Pool) aus, dieses Panel zeigt sie
// nur noch an und rechnet die Euro-Formel darueber - keine eigene KI-Anfrage
// mehr hier (siehe Session-Diskussion 2026-08-06: Auswahl + Einschaetzung sind
// EIN Schritt, die Elternkomponente macht den einen Aufruf, `assessment` kommt
// als fertiges Ergebnis herein).
//
// Danger-Rot ist laut UI_CONVENTIONS.md fuer destruktive Aktionen reserviert,
// kein Zustands-Highlight - "nicht tragfaehig" bleibt deshalb neutral in Ink,
// nicht rot, wie schon die fehlgeschlagenen Laeufe auf der Sources-Seite.

export interface ValuationComparableInput {
  id: string;
  name: string | null;
  admissionsDe: string | null;
  year: number | null;
  /** Weltweit, USD, TMDB - wie budgetUsd/revenueUsd auf TobisTitle. Nur fuer die Erfolgs-Einordnung, fliesst nicht in die Euro-Formel ein. */
  budgetUsd: string | null;
  revenueUsd: string | null;
  /** Vorschlag, ob der Titel in den Schnitt einfliesst - jeder von findComparables gewaehlte Titel startet als vergleichbar, per Toggle uebersteuerbar. */
  comparable: boolean;
  /** Die KI-Begruendung, warum dieser Titel als Vergleichsfilm gewaehlt wurde. */
  reasoning?: string;
  /** OMDb-Poster, sonst TMDB - wie TobisTitle.coverUrl. Fuers Wiedererkennen in der Liste. */
  coverUrl: string | null;
  /** Top-Besetzung (TMDB-Billing-Reihenfolge, siehe titleCast im BFF), max. 5. */
  topCast: readonly ValuationCastMember[];
  /** Aus demselben Cast-Array wie topCast, ueber c.jobs gefiltert statt characterName - siehe creditNames. */
  directors: readonly string[];
  writers: readonly string[];
  /** Heuristische Kennung (db.ts TitleDetail.streamingOriginal) - fuer die Spec-Vorgabe
   *  "Streamer-Originals werden besonders gekennzeichnet" im "TOBIS Algorithmus"-Modus. */
  streamingOriginal: boolean;
}

export interface ValuationCastMember {
  name: string;
  /** TMDB-Personenfoto - haeufig null (v.a. TVmaze-Personen), dann Initialen-Platzhalter. */
  photoUrl: string | null;
}

export interface ComparablesAssessment {
  positive: string | null;
  negative: string | null;
  fazit: string | null;
  suitability: number | null;
}

/** Schnitt ueber die einbezogenen Vergleichstitel, nur zur Einordnung ("wo koennte das landen") - getrennt von der Euro-Formel, die ausschliesslich den Kino-Anteil aus admissionsDe ableitet. */
function average(values: readonly (string | null)[]): number | null {
  const nums = values.map((v) => (v === null ? null : Number(v))).filter((n): n is number => n !== null && Number.isFinite(n));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

interface TobisValuationPanelProps {
  comparables: readonly ValuationComparableInput[];
  /** Explizite Ueberschreibungen je Titel-Id. Fehlt ein Eintrag, gilt comparable als Vorschlag. */
  include: Readonly<Record<string, boolean>>;
  onToggleInclude: (id: string) => void;
  manual: TobisValuationManualInput;
  onManualChange: (next: TobisValuationManualInput) => void;
  /** Ganzheitliche KI-Einschaetzung, schon von der Elternkomponente geholt (findComparables liefert
   *  Auswahl und Einschaetzung in einem Aufruf). null vor dem ersten Lauf oder bei einem Fehlschlag. */
  assessment: ComparablesAssessment | null;
  /** Nutzer-Schaetzung fuers eigene Projekt (Pitch-Schritt), USD wie budgetUsd/revenueUsd auf den
   *  Vergleichstiteln - nur zur Gegenueberstellung mit den Ø-Werten unten, optional. */
  expectedBudgetUsd?: string | null;
  expectedRevenueUsd?: string | null;
}

const MANUAL_FIELDS: ReadonlyArray<{
  key: keyof TobisValuationManualInput;
  labelId: string;
  defaultLabel: string;
}> = [
  { key: "homeEntertainmentCents", labelId: "tobis.lab.valuation.homeEntertainment", defaultLabel: "Home entertainment" },
  { key: "payTvCents", labelId: "tobis.lab.valuation.payTv", defaultLabel: "Pay-TV" },
  { key: "freeTvCents", labelId: "tobis.lab.valuation.freeTv", defaultLabel: "Free-TV" },
  { key: "distributionSubsidyCents", labelId: "tobis.lab.valuation.distributionSubsidy", defaultLabel: "Distribution subsidy" },
  { key: "paBudgetCents", labelId: "tobis.lab.valuation.paBudget", defaultLabel: "P&A budget" },
  { key: "internalCostCents", labelId: "tobis.lab.valuation.internalCost", defaultLabel: "Internal cost" },
];

function centsToInputValue(cents: number): string {
  return cents === 0 ? "" : String(cents / 100);
}

function inputValueToCents(value: string): number {
  const n = Number(value.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

export function TobisValuationPanel({
  comparables,
  include,
  onToggleInclude,
  manual,
  onManualChange,
  assessment,
  expectedBudgetUsd,
  expectedRevenueUsd,
}: TobisValuationPanelProps) {
  const intl = useIntl();
  const [estimate, setEstimate] = useState<TobisValuationEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  // Sichtbarer Fehlschlag statt endlosem "…" (Marvin fand das per Screenshot 2026-08-08: ein
  // 400 von der Route - siehe ValuationEstimateBody.max in routes/tobis.ts - blieb bisher lautlos
  // im catch stehen, die Zahl fehlte einfach, ohne dass klar war warum).
  const [failed, setFailed] = useState(false);
  const lastKey = useRef<string | null>(null);

  const included = comparables.filter((c) => include[c.id] ?? c.comparable);
  const avgAdmissions = average(included.map((c) => c.admissionsDe));
  const avgRevenueUsd = average(included.map((c) => c.revenueUsd));
  const avgBudgetUsd = average(included.map((c) => c.budgetUsd));
  const usd = (n: number) => intl.formatNumber(n, { style: "currency", currency: "USD", notation: "compact" });
  const expectedBudget = expectedBudgetUsd ? Number(expectedBudgetUsd) : null;
  const expectedRevenue = expectedRevenueUsd ? Number(expectedRevenueUsd) : null;

  useEffect(() => {
    const payload: TobisValuationComparable[] = included.map((c) => ({
      id: c.id,
      name: c.name ?? undefined,
      admissionsDe: c.admissionsDe,
      year: c.year,
    }));
    const key = JSON.stringify({ payload, manual });
    const timer = window.setTimeout(() => {
      if (key === lastKey.current) return;
      lastKey.current = key;
      setLoading(true);
      setFailed(false);
      void estimateValuation(payload, manual)
        .then(setEstimate)
        .catch(() => {
          setEstimate(null);
          setFailed(true);
          logApplicationFailure("tobis.lab.valuation.estimate");
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(included.map((c) => c.id)), manual]);

  // Vor dem comparables.length===0-Fruehausstieg extrahiert: der Verdikt-Text
  // der KI ist unabhaengig von einer gelungenen Titel-Aufloesung entstanden
  // (siehe hydrateResultDetail in TobisLabSessionPage.tsx) und soll auch dann
  // sichtbar sein, wenn kein einziger Vergleichstitel aufgeloest werden konnte.
  const assessmentBox =
    assessment?.fazit || assessment?.positive || assessment?.negative ? (
      <div className="mb-6 box-flat smycles-ai-shadow bg-page p-5">
        <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
          <FormattedMessage id="tobis.lab.valuation.assessmentHeading" defaultMessage="AI assessment" />
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-[13px] leading-relaxed text-ink">{assessment.fazit}</p>
          {assessment.suitability !== null ? (
            <span className="shrink-0 tabular-nums text-sm font-medium">{assessment.suitability}%</span>
          ) : null}
        </div>
        {assessment.positive ? (
          <p className="mt-2 text-[12px] leading-relaxed text-ink/65">+ {assessment.positive}</p>
        ) : null}
        {assessment.negative ? (
          <p className="mt-1 text-[12px] leading-relaxed text-ink/65">- {assessment.negative}</p>
        ) : null}
      </div>
    ) : null;

  if (comparables.length === 0) {
    return (
      <div className="space-y-4">
        {assessmentBox}
        <div className="box-flat bg-page p-5 text-sm text-ink/55">
          <FormattedMessage
            id="tobis.lab.valuation.noComparablesResolved"
            defaultMessage="No comparable titles could be matched for this run."
          />
        </div>
      </div>
    );
  }

  const money = (cents: number) =>
    intl.formatNumber(cents / 100, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

  // Literale ids je Fall statt eines Template-Strings: messages-usage.test.ts
  // findet Bundle-Referenzen per Quelltext-Scan, ein zusammengesetzter String
  // waere fuer den Scan unsichtbar und der Key liefe als "verwaist" durch.
  const originLabel = (origin: string) => {
    switch (origin) {
      case "measured":
        return intl.formatMessage({ id: "tobis.lab.valuation.origin.measured", defaultMessage: "measured" });
      case "derived":
        return intl.formatMessage({ id: "tobis.lab.valuation.origin.derived", defaultMessage: "derived" });
      case "estimated":
        return intl.formatMessage({ id: "tobis.lab.valuation.origin.estimated", defaultMessage: "estimated" });
      default:
        return intl.formatMessage({ id: "tobis.lab.valuation.origin.manual", defaultMessage: "manual" });
    }
  };

  // Die Quoten kommen aus der Antwort (model), nicht aus Konstanten hier: sonst
  // nennt der Tooltip nach dem naechsten Modellwechsel Zahlen, mit denen nicht
  // gerechnet wurde.
  const pct = (rate: number) =>
    intl.formatNumber(rate, { style: "percent", maximumFractionDigits: 4 });
  const m = estimate?.model;

  // Der Rechenweg der Verleihmiete, durchgerechnet an EINEM Vergleichsfilm mit
  // den Zahlen dieses Laufs (Marvin, 2026-08-19: "konkret fuer das Beispiel").
  // Die abstrakte Formel allein liess offen, wie aus 12 Filmen ein Betrag wird.
  //
  // Genommen wird der Film mit der hoechsten Besucherzahl: er tut am meisten zum
  // Schnitt, und seine Zahlen sind die groessten - an ihnen laesst sich am
  // leichtesten pruefen, ob die Kette stimmt.
  //
  // Bewusst NICHT der Weg "Ø Besucher × Ø Ticketpreis": das ist nicht dieselbe
  // Rechnung. Jeder Film wird mit dem Preis SEINES Jahrgangs gerechnet, und der
  // Schnitt aus zwoelf Ergebnissen ist nicht das Ergebnis aus zwoelf Schnitten.
  // Eine solche "Beispielrechnung" ergaebe eine Zahl, die unten nicht steht.
  const steps = estimate?.cinemaShareSteps ?? [];
  const beispiel = steps.reduce<(typeof steps)[number] | undefined>(
    (best, s) => (best === undefined || s.admissions > best.admissions ? s : best),
    undefined,
  );
  const euro = (cents: number) =>
    intl.formatNumber(cents / 100, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  const euroExakt = (cents: number) =>
    intl.formatNumber(cents / 100, { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

  const cinemaShareFormula = m ? (
    <>
      <FormattedMessage
        id="tobis.lab.valuation.formula.cinemaShare"
        defaultMessage="Per comparable: DE admissions × the average ticket price of its release year = gross box office. Take out {vat} VAT, then {ffa} FFA levy and {gema} GEMA/GVL from the net, and {share} of what remains is the distributor's share. Shown here is the average across the {count} comparables that have a DE admission count."
        values={{
          vat: pct(m.vatRate),
          ffa: pct(m.ffaLevyRate),
          gema: pct(m.gemaGvlRate),
          share: pct(m.distributorShareRate),
          count: estimate?.comparablesUsed ?? 0,
        }}
      />
      {beispiel ? (
        <span className="mt-2 block border-t border-on-ink/20 pt-2">
          <span className="block font-medium">
            <FormattedMessage
              id="tobis.lab.valuation.formula.exampleHeading"
              defaultMessage="Worked example: {name}"
              values={{ name: beispiel.name ?? beispiel.id }}
            />
          </span>
          <span className="mt-1 block tabular-nums">
            {[
              intl.formatMessage(
                {
                  id: "tobis.lab.valuation.formula.step.gross",
                  defaultMessage:
                    "{admissions} admissions × {price} ({priceYear}) = {gross} gross",
                },
                {
                  admissions: intl.formatNumber(beispiel.admissions),
                  price: euroExakt(beispiel.ticketPriceCents),
                  priceYear:
                    beispiel.ticketPriceFromYear === beispiel.year
                      ? String(beispiel.year)
                      : intl.formatMessage(
                          {
                            id: "tobis.lab.valuation.formula.step.priceSubstitute",
                            defaultMessage: "{year}, nearest year on record: {fromYear}",
                          },
                          { year: beispiel.year, fromYear: beispiel.ticketPriceFromYear },
                        ),
                  gross: euro(beispiel.grossCents),
                },
              ),
              intl.formatMessage(
                {
                  id: "tobis.lab.valuation.formula.step.net",
                  defaultMessage: "− {vat} VAT = {net} net",
                },
                { vat: pct(m.vatRate), net: euro(beispiel.netCents) },
              ),
              intl.formatMessage(
                {
                  id: "tobis.lab.valuation.formula.step.levies",
                  defaultMessage: "− {ffa} FFA − {gema} GEMA/GVL = {afterLevies}",
                },
                {
                  ffa: pct(m.ffaLevyRate),
                  gema: pct(m.gemaGvlRate),
                  // Vom Server, nicht hier gerechnet: dieselbe Formel an zwei
                  // Orten liefe beim naechsten Modellwechsel auseinander.
                  afterLevies: euro(beispiel.afterLeviesCents),
                },
              ),
              intl.formatMessage(
                {
                  id: "tobis.lab.valuation.formula.step.share",
                  defaultMessage: "× {share} distributor share = {result}",
                },
                { share: pct(m.distributorShareRate), result: euro(beispiel.cinemaShareCents) },
              ),
            ].map((zeile, i) => (
              <span key={i} className="block">
                {zeile}
              </span>
            ))}
          </span>
          <span className="mt-1.5 block text-on-ink/70">
            <FormattedMessage
              id="tobis.lab.valuation.formula.exampleAverage"
              defaultMessage="The same for the other {others, plural, one {# comparable} other {# comparables}}; the average of those {count} amounts is the {total} above (range {min} to {max})."
              values={{
                others: steps.length - 1,
                count: steps.length,
                total: money(estimate?.revenue.cinemaShare.cents ?? 0),
                min: euro(Math.min(...steps.map((x) => x.cinemaShareCents))),
                max: euro(Math.max(...steps.map((x) => x.cinemaShareCents))),
              }}
            />
          </span>
        </span>
      ) : null}
    </>
  ) : undefined;

  const manualFormula = (
    <FormattedMessage
      id="tobis.lab.valuation.formula.manual"
      defaultMessage="No source in the catalogue for this line: the value is typed in and enters the calculation unchanged. Left at 0 € it excludes the channel."
    />
  );

  // Was dieser eine Titel an der Zahl bewirkt. Drei Faelle, und der dritte ist
  // der, der ohne Erklaerung wie ein Fehler aussieht: ein Titel steht in der
  // Liste, bewegt die Grenze aber nicht, weil ihm die deutsche Besucherzahl
  // fehlt - die einzige Groesse, aus der die Rechnung den Kinoumsatz zieht.
  const contribution = (c: ValuationComparableInput, isIncluded: boolean): ReactNode => {
    if (!isIncluded) {
      return (
        <FormattedMessage
          id="tobis.lab.valuation.contribution.excluded"
          defaultMessage="Currently excluded - does not affect the ceiling."
        />
      );
    }
    // Vor der ersten Antwort (die Rechnung laeuft mit 300 ms Verzoegerung, siehe
    // useEffect) ist noch nichts bekannt. Ohne diesen Zweig behauptete das Pop-up
    // in dieser Zeit "bewegt die Grenze nicht" - eine falsche Aussage, die im Bild
    // nicht auffaellt, weil ein Pop-up erst beim Zeigen erscheint.
    if (!estimate) {
      return (
        <FormattedMessage
          id="tobis.lab.valuation.contribution.calculating"
          defaultMessage="Being calculated…"
        />
      );
    }
    const own = estimate.perComparable.find((x) => x.id === c.id);
    if (!own) {
      return (
        <FormattedMessage
          id="tobis.lab.valuation.contribution.noAdmissions"
          defaultMessage="Counts as context only: without a German admission count and a release year there is no box office figure to derive, so this title does not move the ceiling."
        />
      );
    }
    return (
      <FormattedMessage
        id="tobis.lab.valuation.contribution.own"
        defaultMessage="{admissions} DE admissions ({year}). On this title alone the calculation would give a ceiling of {ceiling}; the figure above is the average across all included titles."
        values={{
          admissions: intl.formatNumber(Number(c.admissionsDe ?? 0)),
          year: c.year ?? "?",
          ceiling: money(own.maxPurchasePrice),
        }}
      />
    );
  };

  return (
    <div className="box bg-surface p-6">
      {assessmentBox}

      {/* Grobe Erfolgs-Einordnung ueber die Vergleichstitel, getrennt von der
          Euro-Formel oben: budgetUsd/revenueUsd sind TMDB, weltweit, USD -
          fliessen bewusst nicht in maxPurchasePrice ein (der rechnet nur den
          deutschen Kino-Anteil), sagen aber, in welcher Groessenordnung
          vergleichbare Titel gelandet sind. */}
      {included.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-ink/10 pt-4 text-[12px] text-ink/55">
          {/* Eine Erklaerung fuer die ganze Zeile statt fuenf: die Frage ist bei
              allen Werten dieselbe, und sie ist die haeufigste ueberhaupt - warum
              die Kaufpreisgrenze bei zwei Millionen Euro liegt, wenn hier 190
              Millionen Dollar stehen. */}
          <span className="print:hidden">
            <InfoTooltip
              ariaLabel={intl.formatMessage({
                id: "tobis.lab.valuation.benchmarkInfo",
                defaultMessage: "What these averages mean",
              })}
              title={
                <FormattedMessage
                  id="tobis.lab.valuation.benchmarkInfo"
                  defaultMessage="What these averages mean"
                />
              }
            >
              <FormattedMessage
                id="tobis.lab.valuation.formula.benchmark"
                defaultMessage="Averages across the {count} considered comparables, each over the titles that carry the figure. Revenue and budget are TMDB, worldwide, in dollars - they place the project in an order of magnitude and deliberately do NOT enter the bid ceiling, which only ever calculates the German cinema share. That is why a title with 190M$ worldwide can still land at a ceiling of two million euros."
                values={{ count: included.length }}
              />
            </InfoTooltip>
          </span>
          <span>
            <FormattedMessage id="tobis.lab.valuation.avgAdmissions" defaultMessage="Ø DE admissions" />{" "}
            <span className="tabular-nums text-ink">
              {avgAdmissions === null ? "…" : intl.formatNumber(avgAdmissions, { notation: "compact" })}
            </span>
          </span>
          <span>
            <FormattedMessage id="tobis.lab.valuation.avgRevenue" defaultMessage="Ø revenue (worldwide)" />{" "}
            <span className="tabular-nums text-ink">{avgRevenueUsd === null ? "…" : usd(avgRevenueUsd)}</span>
          </span>
          {expectedRevenue !== null ? (
            <span>
              <FormattedMessage id="tobis.lab.valuation.yourRevenue" defaultMessage="Your expected revenue" />{" "}
              <span className="tabular-nums text-ink">{usd(expectedRevenue)}</span>
            </span>
          ) : null}
          <span>
            <FormattedMessage id="tobis.lab.valuation.avgBudget" defaultMessage="Ø budget" />{" "}
            <span className="tabular-nums text-ink">{avgBudgetUsd === null ? "…" : usd(avgBudgetUsd)}</span>
          </span>
          {expectedBudget !== null ? (
            <span>
              <FormattedMessage id="tobis.lab.valuation.yourBudget" defaultMessage="Your budget" />{" "}
              <span className="tabular-nums text-ink">{usd(expectedBudget)}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      {estimate ? (
        <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-1 text-[12px] text-ink/65 @sm:grid-cols-2">
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.cinemaShare", defaultMessage: "Cinema share" })}
            amount={estimate.revenue.cinemaShare}
            money={money}
            originLabel={originLabel}
            formula={cinemaShareFormula}
          />
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.homeEntertainment", defaultMessage: "Home entertainment" })}
            amount={estimate.revenue.homeEntertainment}
            money={money}
            originLabel={originLabel}
            formula={manualFormula}
          />
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.payTv", defaultMessage: "Pay-TV" })}
            amount={estimate.revenue.payTv}
            money={money}
            originLabel={originLabel}
            formula={manualFormula}
          />
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.freeTv", defaultMessage: "Free-TV" })}
            amount={estimate.revenue.freeTv}
            money={money}
            originLabel={originLabel}
            formula={manualFormula}
          />
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.distributionSubsidy", defaultMessage: "Distribution subsidy" })}
            amount={estimate.revenue.distributionSubsidy}
            money={money}
            originLabel={originLabel}
            formula={manualFormula}
          />
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.paBudget", defaultMessage: "P&A budget" })}
            amount={estimate.cost.paBudget}
            money={money}
            originLabel={originLabel}
            formula={manualFormula}
            negative
          />
          <BreakdownLine
            label={intl.formatMessage({ id: "tobis.lab.valuation.internalCost", defaultMessage: "Internal cost" })}
            amount={estimate.cost.internalCost}
            money={money}
            originLabel={originLabel}
            formula={manualFormula}
            negative
          />
          <div className="flex items-center justify-between border-t border-ink/10 pt-1 font-medium text-ink">
            <span>
              <FormattedMessage id="tobis.lab.valuation.margin" defaultMessage="Target margin (10%)" />{" "}
              <span className="print:hidden">
                <InfoTooltip
                  ariaLabel={intl.formatMessage({
                    id: "tobis.lab.valuation.margin",
                    defaultMessage: "Target margin (10%)",
                  })}
                  title={
                    <FormattedMessage
                      id="tobis.lab.valuation.formulaTitle"
                      defaultMessage="How this is calculated"
                    />
                  }
                >
                  <FormattedMessage
                    id="tobis.lab.valuation.formula.margin"
                    defaultMessage="{rate} of P&A budget + internal cost + subtotal ({base}). That sum equals total revenue; the form follows the appraisal guide so it stays checkable against the document."
                    values={{
                      rate: m ? pct(m.marginRate) : "",
                      base: money(estimate.valuation.marginBase),
                    }}
                  />
                </InfoTooltip>
              </span>
            </span>
            <span className="tabular-nums">-{money(estimate.valuation.margin)}</span>
          </div>
        </div>
      ) : null}

      {/* Ergebnis bewusst NACH dem Rechenweg/den Abzuegen (Marvin, 2026-08-07):
          zuerst die Herleitung, dann die Summe - wie eine Rechnung, nicht wie
          eine Schlagzeile mit Kleingedrucktem danach. */}
      <div className="mt-5 flex flex-wrap items-baseline justify-between gap-3 border-t border-ink/10 pt-5">
        <div className="text-[12px] text-ink/55">
          {estimate ? (
            estimate.valuation.viable ? (
              <FormattedMessage id="tobis.lab.valuation.viable" defaultMessage="Viable at this price" />
            ) : (
              <FormattedMessage id="tobis.lab.valuation.notViable" defaultMessage="Not viable at this price" />
            )
          ) : loading ? (
            <FormattedMessage id="tobis.lab.aiWorking" defaultMessage="Working…" />
          ) : failed ? (
            <FormattedMessage
              id="tobis.lab.valuation.estimateFailed"
              defaultMessage="Could not be calculated - try again."
            />
          ) : null}
          {estimate ? (
            <div className="mt-1">
              <FormattedMessage
                id="tobis.lab.valuation.comparablesUsed"
                defaultMessage="{count, plural, one {# comparable with DE admissions} other {# comparables with DE admissions}}"
                values={{ count: estimate.comparablesUsed }}
              />{" "}
              <span className="print:hidden">
                <InfoTooltip
                  ariaLabel={intl.formatMessage({
                    id: "tobis.lab.valuation.comparablesUsedInfo",
                    defaultMessage: "Which comparables the calculation uses",
                  })}
                  title={
                    <FormattedMessage
                      id="tobis.lab.valuation.comparablesUsedInfo"
                      defaultMessage="Which comparables the calculation uses"
                    />
                  }
                >
                  <FormattedMessage
                    id="tobis.lab.valuation.formula.comparablesUsed"
                    defaultMessage="Of {considered} considered titles, {used} carry both a German admission count and a release year - only those can produce a box office figure. The rest stay in the list as context and do not move the number."
                    values={{ considered: included.length, used: estimate.comparablesUsed }}
                  />
                </InfoTooltip>
              </span>
            </div>
          ) : null}
        </div>
        {/* Rechtsbuendig (Marvin, 2026-08-07): landet damit unter der
            Zahlenspalte des Rechenwegs oben, statt allein am linken Rand zu
            stehen, waehrend die Betraege drueber rechts sitzen. */}
        <div className="text-right">
          <div className="text-[10px] tracking-[0.25em] text-ink/55">
            <FormattedMessage id="tobis.lab.valuation.heading" defaultMessage="Estimated bid ceiling" />
            {estimate ? (
              <span className="ml-1 align-middle print:hidden">
                <InfoTooltip
                  placement="left"
                  ariaLabel={intl.formatMessage({
                    id: "tobis.lab.valuation.formulaTitle",
                    defaultMessage: "How this is calculated",
                  })}
                  title={
                    <FormattedMessage
                      id="tobis.lab.valuation.formulaTitle"
                      defaultMessage="How this is calculated"
                    />
                  }
                >
                  <FormattedMessage
                    id="tobis.lab.valuation.formula.ceiling"
                    defaultMessage="Revenue {revenue} − cost {cost} = subtotal {subtotal}, minus the target margin {margin}. What remains is what the title may cost at most. Model {version}."
                    values={{
                      revenue: money(estimate.valuation.revenueTotal),
                      cost: money(estimate.valuation.costTotal),
                      subtotal: money(estimate.valuation.subtotal),
                      margin: money(estimate.valuation.margin),
                      version: estimate.valuation.modelVersion,
                    }}
                  />
                </InfoTooltip>
              </span>
            ) : null}
          </div>
          <div className="mt-1 font-serif text-4xl leading-none tracking-tight">
            {estimate ? money(estimate.valuation.maxPurchasePrice) : "…"}
          </div>
        </div>
      </div>

      {estimate ? (
        <TobisValuationDistribution
          perComparable={estimate.perComparable}
          costCents={estimate.valuation.costTotal}
          money={money}
        />
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-3 @sm:grid-cols-2 @lg:grid-cols-3">
        {MANUAL_FIELDS.map((f) => (
          <div key={f.key} className="box-flat bg-page p-3">
            <div className="mb-1 text-[10px] tracking-[0.2em] text-ink/55">
              <FormattedMessage id={f.labelId} defaultMessage={f.defaultLabel} />
            </div>
            <div className="flex items-center gap-1">
              <input
                type="text"
                inputMode="decimal"
                value={centsToInputValue(manual[f.key])}
                onChange={(e) =>
                  onManualChange({ ...manual, [f.key]: inputValueToCents(e.target.value) })
                }
                placeholder="0"
                className="w-full bg-transparent text-sm tabular-nums outline-none placeholder:text-ink/30"
              />
              <span className="text-[11px] text-ink/40">€</span>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-ink/40">
        <FormattedMessage
          id="tobis.lab.valuation.manualHint"
          defaultMessage="No source in the catalogue for these six: enter them by hand, or leave at 0 to exclude the channel."
        />
      </p>

      <div className="mt-6">
        <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/55">
          <FormattedMessage id="tobis.lab.valuation.comparablesHeading" defaultMessage="Considered comparables" />
        </div>
        <div className="space-y-2">
          {comparables.map((c) => {
            const isIncluded = include[c.id] ?? c.comparable;
            return (
              // group + -mx-2/px-2 statt Hover nur auf dem Link: die ganze Zeile soll
              // aufleuchten und klickbar sein (Marvin, 2026-08-07), nicht nur der
              // Titeltext - der Toggle-Button bleibt bewusst AUSSERHALB des Links
              // (verschachtelte <a>/<button> waeren ungueltiges HTML und wuerden bei
              // einem Klick beide Handler feuern).
              <div
                key={c.id}
                className="group -mx-2 flex items-start justify-between gap-3 rounded px-2 py-1.5 text-[12px] transition hover:bg-ink/[0.04]"
              >
                <Link to={`/tobis/titles/${c.id}`} className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="h-[4.5rem] w-12 shrink-0 overflow-hidden rounded bg-ink/5">
                    {c.coverUrl ? (
                      <img src={c.coverUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                    ) : null}
                  </span>
                  <span className="min-w-0">
                    <span className="truncate text-ink/70 group-hover:text-ink group-hover:underline">
                      {c.name ?? c.id}
                    </span>
                    {c.streamingOriginal ? (
                      <span className="ml-2 shrink-0 rounded-full bg-accent/10 px-1.5 py-0.5 text-[9px] font-medium tracking-wide text-accent">
                        <FormattedMessage
                          id="tobis.lab.valuation.streamingOriginal"
                          defaultMessage="Streaming Original"
                        />
                      </span>
                    ) : null}
                    <span className="ml-2 text-ink/40 tabular-nums">
                      {c.admissionsDe ? (
                        <>
                          {intl.formatNumber(Number(c.admissionsDe), { notation: "compact" })} DE
                          {c.year ? ` · ${c.year}` : ""}
                        </>
                      ) : (
                        <FormattedMessage id="tobis.lab.valuation.noAdmissions" defaultMessage="no DE admissions" />
                      )}
                      {c.revenueUsd ? (
                        <>
                          {" · "}
                          {intl.formatMessage(
                            { id: "tobis.lab.valuation.compRevenue", defaultMessage: "{amount} revenue" },
                            {
                              amount: intl.formatNumber(Number(c.revenueUsd), {
                                style: "currency",
                                currency: "USD",
                                notation: "compact",
                              }),
                            },
                          )}
                        </>
                      ) : null}
                      {c.budgetUsd ? (
                        <>
                          {" · "}
                          {intl.formatMessage(
                            { id: "tobis.lab.valuation.compBudget", defaultMessage: "{amount} budget" },
                            {
                              amount: intl.formatNumber(Number(c.budgetUsd), {
                                style: "currency",
                                currency: "USD",
                                notation: "compact",
                              }),
                            },
                          )}
                        </>
                      ) : null}
                    </span>
                    {c.directors.length > 0 || c.writers.length > 0 ? (
                      <div className="mt-1 text-[11px] text-ink/45">
                        {c.directors.length > 0 ? (
                          <FormattedMessage
                            id="tobis.lab.valuation.directorCredit"
                            defaultMessage="Director: {names}"
                            values={{ names: c.directors.join(", ") }}
                          />
                        ) : null}
                        {c.directors.length > 0 && c.writers.length > 0 ? " · " : ""}
                        {c.writers.length > 0 ? (
                          <FormattedMessage
                            id="tobis.lab.valuation.writerCredit"
                            defaultMessage="Writer: {names}"
                            values={{ names: c.writers.join(", ") }}
                          />
                        ) : null}
                      </div>
                    ) : null}
                    {c.topCast.length > 0 ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {c.topCast.map((member) => (
                          <span key={member.name} className="flex items-center gap-1">
                            {member.photoUrl ? (
                              <img
                                src={member.photoUrl}
                                alt=""
                                loading="lazy"
                                className="h-5 w-5 shrink-0 rounded-full object-cover"
                              />
                            ) : (
                              <span
                                aria-hidden
                                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-serif text-[8px]"
                                style={AVATAR_PLACEHOLDER_STYLE}
                              >
                                {avatarInitials(member.name)}
                              </span>
                            )}
                            <span className="text-[11px] text-ink/40">{member.name}</span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {c.reasoning ? (
                      <p className="mt-0.5 text-[11px] leading-snug text-ink/45">{c.reasoning}</p>
                    ) : null}
                    {/* "Warum steht der hier" war die haeufigste offene Frage am
                        Ergebnis (Marvin, 2026-08-19). Die KI-Begruendung allein
                        beantwortet sie halb: sie sagt, warum der Titel gewaehlt
                        wurde, aber nicht, was er an der Zahl bewirkt. Beides
                        gehoert zusammen, deshalb ein Pop-up mit der Begruendung
                        UND dem eigenen Rechenergebnis dieses Titels. */}
                    <span className="mt-0.5 inline-block print:hidden">
                      <InfoTooltip
                        ariaLabel={intl.formatMessage(
                          { id: "tobis.lab.valuation.whyThisTitle", defaultMessage: "Why this title?" },
                        )}
                        title={
                          <FormattedMessage
                            id="tobis.lab.valuation.whyThisTitle"
                            defaultMessage="Why this title?"
                          />
                        }
                        contentClassName="max-w-xs"
                      >
                        <span className="block">
                          {c.reasoning ? (
                            c.reasoning
                          ) : (
                            <FormattedMessage
                              id="tobis.lab.valuation.whyNoReason"
                              defaultMessage="The model returned no reason for this title. It came in either as a title named in the pitch or from the theme pool."
                            />
                          )}
                        </span>
                        <span className="mt-1.5 block text-ink/55">{contribution(c, isIncluded)}</span>
                      </InfoTooltip>
                    </span>
                  </span>
                </Link>
                {/* variant="secondary" statt "toggle": das Label selbst wechselt
                    zwischen zwei klar unterscheidbaren Zustaenden (Haken +
                    Verb vs. reiner Call-to-Action), traegt das Zustandssignal
                    also schon - siehe UI_CONVENTIONS.md "visual flip vs.
                    label-only" (dasselbe Muster wie ShareItemDetailPage's
                    "✓ Watching - remove"). "toggle" waere hier redundant und
                    liess den Klick zuvor missverstehen: das Label zeigte nur
                    den AKTUELLEN Zustand ("Ausgeschlossen"), nicht was der
                    Klick bewirkt. */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onToggleInclude(c.id)}
                  className="shrink-0 print:hidden"
                >
                  {isIncluded ? (
                    <FormattedMessage id="tobis.lab.valuation.included" defaultMessage="✓ Considered - remove" />
                  ) : (
                    <FormattedMessage id="tobis.lab.valuation.excluded" defaultMessage="Consider" />
                  )}
                </Button>
                {/* Bildschirm zeigt den Ausschluss schon ueber den Button-Zustand oben - der
                    faellt beim Drucken weg (print:hidden), deshalb hier ein reiner Text als
                    Ersatz, sonst liest sich die gedruckte Liste wie "alle einbezogen". */}
                {!isIncluded ? (
                  <span className="hidden shrink-0 text-[11px] text-ink/40 print:inline">
                    <FormattedMessage id="tobis.lab.valuation.excludedPrintNote" defaultMessage="(excluded)" />
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BreakdownLine({
  label,
  amount,
  money,
  originLabel,
  formula,
  negative = false,
}: {
  label: string;
  amount: { cents: number; origin: string; source?: string };
  money: (cents: number) => string;
  originLabel: (origin: string) => string;
  /** Wie dieser Posten zustande kommt. Steht im selben Pop-up wie die Quelle:
   *  zwei Icons je Zeile waeren zwei Klickziele fuer eine Frage. */
  formula?: ReactNode;
  negative?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>
        {label} <span className="text-ink/35">({originLabel(amount.origin)})</span>
        {/* Echtes Pop-Up statt eines nackten title-Attributs (Marvin, 2026-08-08: Spec-
            Vorgabe "im Idealfall ... die direkten Quellen ... per Pop-Up") - print:hidden,
            weil ein Hover-Pop-up auf Papier ohnehin nichts zeigen kann; die Quelle bleibt
            beim Drucken einfach unsichtbar wie bisher jeder Tooltip. */}
        {amount.source || formula ? (
          <span className="print:hidden">
            <InfoTooltip
              ariaLabel={`${label}: ${amount.source ?? ""}`}
              title={
                formula ? (
                  <FormattedMessage id="tobis.lab.valuation.formulaTitle" defaultMessage="How this is calculated" />
                ) : (
                  <FormattedMessage id="tobis.lab.valuation.sourceTitle" defaultMessage="Source" />
                )
              }
            >
              {formula}
              {formula && amount.source ? (
                <span className="mt-1.5 block text-ink/55">
                  <FormattedMessage id="tobis.lab.valuation.sourceTitle" defaultMessage="Source" />
                  {": "}
                  {amount.source}
                </span>
              ) : (
                amount.source
              )}
            </InfoTooltip>
          </span>
        ) : null}
      </span>
      <span className="shrink-0 tabular-nums">
        {negative ? "-" : ""}
        {money(amount.cents)}
      </span>
    </div>
  );
}
